import { useAtomValue } from "jotai";
import first from "lodash/first";
import { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { section } from "shared/data";
import { useResponsive } from "shared/hooks";
import { accountAtom } from "shared/lib/account/atoms";
import { theme } from "shared/lib/styles";

import { appAtom } from "scmp-app/lib/app/atoms";
import { rosettaAtom } from "scmp-app/lib/rosetta/atoms";
import type { TypeformConfigKey } from "scmp-app/lib/typeform/consts";
import type { hooksReadMoreTypeformContent$key } from "scmp-app/queries/__generated__/hooksReadMoreTypeformContent.graphql";

export const usePlusSurveyTypeFormConfig = () => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const [configKey, setConfigKey] = useState<TypeformConfigKey>();

  const isPlusSubscriber = asyncRosettaState?.result?.isScmpPlusSubscriber;
  const isSCMPSubscriber = asyncRosettaState?.result?.isScmpSubscriber;

  useEffect(() => {
    if (asyncRosettaState?.status !== "success") return;
    if (isPlusSubscriber) {
      setConfigKey("PLUS_SURVEY_FOR_PLUS_SUBSCRIBERS");
    } else if (isSCMPSubscriber) {
      setConfigKey("PLUS_SURVEY_FOR_NON_PLUS_SUBSCRIBERS");
    }
  }, [asyncRosettaState?.status, isPlusSubscriber, isSCMPSubscriber]);

  return {
    configKey,
  };
};

type UseReadMoreTypeformProps = {
  reference: hooksReadMoreTypeformContent$key;
};

export const useReadMoreTypeform = ({ reference }: UseReadMoreTypeformProps) => {
  const content = useFragment(
    graphql`
      fragment hooksReadMoreTypeformContent on Content {
        entityId
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
            ... on VideoSection {
              entityId
            }
          }
        }
      }
    `,
    reference,
  );

  const { user } = useAtomValue(accountAtom);

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const [shouldOpen, setShouldOpen] = useState(false);

  const firstSectionEntityIds = useMemo(
    () => first(content.sectionsV2)?.value?.map(section => section?.entityId ?? "") ?? [],
    [content.sectionsV2],
  );
  const { advertisementConfig } = useAtomValue(appAtom);

  const isSurveyEnabled = advertisementConfig?.mobileInlineAdTest?.isSurveyEnabled ?? false;

  const triggerOpen = useCallback(() => {
    const isEconomyArticle = firstSectionEntityIds.includes(section.economy.entityId);

    if (!isDesktopUp && isSurveyEnabled && isEconomyArticle) {
      setShouldOpen(true);
    }
  }, [isDesktopUp, isSurveyEnabled, firstSectionEntityIds]);

  return {
    configKey: shouldOpen ? ("READ_MORE_SURVEY" as TypeformConfigKey) : undefined,
    triggerOpen,
    utm_content: content.entityId,
    utm_source: null,
    utm_term: user?.uuid,
  };
};
